import Vue from 'vue';
import { axios } from '@/axios.config';
import { mockAdapter } from '@/mocks/axiosMockAdapter.config';

export const state = {
  data: [],
  selectedId: '',
  details: {},
  requests: {},
  allAccounts: [],
};

export const actions = {
  /**
   * Fetch Groups
   */
  fetchGroups({ state, commit, rootState }, { force = false }) {
    if (!force) {
      if (state.data.length) return Promise.resolve();
    }

    const url = 'account/group';
    const params = { params: { vendor: rootState.vendor } };

    mockAdapter(axios, 'get', url, 'getGroupsAccount', 200);

    return axios.get(url, params).then(res => {
      commit('fetchGroupsSucceed', res.data);

      return Promise.resolve();
    });
  },

  /**
   * Fetch Group Details
   * @param force
   * @returns {Promise<void>}
   */
  async fetchGroupDetails({ state, commit, rootState }, force) {
    if (!state.selectedId) return Promise.resolve();

    const reqHash = state.selectedId;
    const id = state.selectedId;
    const url = `/account/group/${id}`;

    if (!force && state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    const cancelToken = new axios.CancelToken(() => {});

    mockAdapter(axios, 'get', url, 'getGroupAccountDetail', 200);

    commit('fetchStart', { reqHash, cancelToken });

    const { data } = await axios.get(url, { params: { vendor: rootState.vendor } });

    commit('usageReports/updateDetail', { data, reqHash }, { root: true });

    commit('fetchGroupDetailsSucceed', { data, reqHash });
    commit('fetchSucceed', { reqHash });

    return Promise.resolve();
  },

  async create({ state, commit, dispatch, rootState }, datasets) {
    let url, body;

    if (datasets['type'] === 'account') {
      url = '/account/group';
      body = {
        group_name: datasets['name'],
        accounts: datasets['data'],
        vendor: rootState.vendor,
      };
    }

    try {
      await axios.post(url, body);
      await dispatch('fetchGroups', { force: true });
      const newGroup = await state.data.find(grp => grp.group_name === datasets['name']);
      if (newGroup) {
        await commit('selectGroup', newGroup.group_id);
      }
      return Promise.resolve();
    } catch (e) {
      throw new Error(e);
    }
  },

  async update({ dispatch, rootState }, datasets) {
    let url, body;

    url = `/account/group/${datasets.id}`;
    body = {
      group_name: datasets.name,
      accounts: datasets.data,
      vendor: rootState.vendor,
    };

    await axios.put(url, body);
    await dispatch('fetchGroups', { force: true });
  },

  async delete({ commit, rootState }, { id }) {
    const url = `account/group/${id}`;

    try {
      await axios.delete(url, { params: { vendor: rootState.vendor } });
      await commit('updateSelectedId', null);
      return Promise.resolve();
    } catch (e) {
      throw new Error('Error');
    }
  },
};

export const mutations = {
  selectGroup(state, id) {
    state.selectedId = id;
  },

  updateSelectedId(state, id) {
    state.selectedId = id;
  },

  fetchGroupsSucceed(state, data) {
    state.data = data;
  },

  fetchGroupDetailsSucceed(state, { data, reqHash }) {
    Vue.set(state.details, reqHash, data);
  },

  fetchStart(state, { reqHash, cancelToken }) {
    Vue.set(state.requests, reqHash, {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },
  fetchSucceed(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },
};

export const getters = {
  getGroups(state) {
    return state.data ? state.data : [];
  },

  getGroupDetail(state) {
    const id = state.selectedId;

    return state.details[id] ? state.details[id] : null;
  },

  getGroupDetailName(state, getters) {
    const detail = getters.getGroupDetail;
    return detail ? detail.group_name : '';
  },

  getGroupDetailId(state, getters) {
    const detail = getters.getGroupDetail;
    return detail ? detail.group_id : '';
  },

  getGroupDetailItems(state, getters, rootState, rootGetters) {
    const detail = getters.getGroupDetail;
    if (!detail) return [];

    const allAccounts = rootGetters['accounts/getAccounts'];

    return detail.accounts.map(accId => {
      return allAccounts.find(i => i.account_id === accId);
    });
  },

  mappingGroupData(state) {
    return state.data.map(val => {
      return {
        group_id: val.group_id,
        group_name: val.group_name,
      };
    });
  },
};
